/**
 * Testimonial showcase.
 * One testimonial is open at a time; the two before and the two after it show as small photo cards.
 * Opens a testimonial on click, arrow keys or swipe, and advances automatically when `data-autoplay`
 * holds a number of seconds greater than zero.
 */
class TestimonialShowcase extends HTMLElement {
  /** @type {number} */
  #index = 0;

  /** @type {number | undefined} */
  #timer;

  /** @type {number | null} */
  #touchStartX = null;

  /** @type {number | null} */
  #touchStartY = null;

  #abortController = new AbortController();

  connectedCallback() {
    this.cards = /** @type {HTMLElement[]} */ (Array.from(this.querySelectorAll('[data-card]')));
    this.dots = /** @type {HTMLElement[]} */ (Array.from(this.querySelectorAll('[data-dots] [data-open]')));
    this.strip = /** @type {HTMLElement | null} */ (this.querySelector('[data-strip]'));
    this.stripItems = /** @type {HTMLElement[]} */ (Array.from(this.querySelectorAll('[data-strip] [data-open]')));
    this.stage = /** @type {HTMLElement | null} */ (this.querySelector('[data-stage]'));

    if (this.cards.length === 0) return;

    const { signal } = this.#abortController;
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.autoplaySeconds = reducedMotion ? 0 : Number(this.dataset.autoplay) || 0;

    this.addEventListener('click', this.#handleClick, { signal });
    this.addEventListener('keydown', this.#handleKeydown, { signal });
    this.addEventListener('mouseenter', () => this.#stop(), { signal });
    this.addEventListener('mouseleave', () => this.#start(), { signal });
    this.addEventListener('focusin', () => this.#stop(), { signal });
    this.addEventListener('focusout', () => this.#start(), { signal });
    this.stage?.addEventListener('touchstart', this.#handleTouchStart, { signal, passive: true });
    this.stage?.addEventListener('touchend', this.#handleTouchEnd, { signal, passive: true });
    document.addEventListener('visibilitychange', this.#handleVisibility, { signal });

    this.#render();
    this.#start();
  }

  disconnectedCallback() {
    this.#stop();
    this.#abortController.abort();
  }

  /**
   * Opens the testimonial at the given position, wrapping around at both ends.
   * @param {number} index
   */
  show(index) {
    const count = this.cards.length;
    this.#index = ((index % count) + count) % count;
    this.#render();
  }

  #render() {
    const count = this.cards.length;

    this.cards.forEach((card, i) => {
      // Signed distance from the open card, taking the shorter way round the loop
      let offset = (((i - this.#index) % count) + count) % count;
      if (offset > count / 2) offset -= count;

      card.dataset.offset = Math.abs(offset) <= 2 ? String(offset) : 'hidden';
      card.style.order = String(offset);
      card.querySelector('.tshow__text')?.setAttribute('aria-hidden', String(offset !== 0));
    });

    for (const items of [this.dots, this.stripItems]) {
      items.forEach((item, i) => item.setAttribute('aria-current', String(i === this.#index)));
    }

    // Keep the open customer's photo in view inside the strip without scrolling the page
    const activeItem = this.stripItems[this.#index];
    if (this.strip && activeItem && this.strip.scrollWidth > this.strip.clientWidth) {
      const left = activeItem.offsetLeft - (this.strip.clientWidth - activeItem.offsetWidth) / 2;
      this.strip.scrollTo({ left, behavior: 'smooth' });
    }
  }

  #start() {
    this.#stop();
    if (!this.autoplaySeconds || this.cards.length < 2 || document.hidden) return;
    this.#timer = window.setInterval(() => this.show(this.#index + 1), this.autoplaySeconds * 1000);
  }

  #stop() {
    window.clearInterval(this.#timer);
    this.#timer = undefined;
  }

  /** @param {MouseEvent} event */
  #handleClick = (event) => {
    const target = event.target instanceof Element ? event.target.closest('[data-open]') : null;
    if (!(target instanceof HTMLElement)) return;

    this.show(Number(target.dataset.open));
    // Restart the wait so the chosen testimonial gets its full time on screen
    if (this.#timer) this.#start();
  };

  /** @param {KeyboardEvent} event */
  #handleKeydown = (event) => {
    if (event.key === 'ArrowRight') this.show(this.#index + 1);
    else if (event.key === 'ArrowLeft') this.show(this.#index - 1);
  };

  /** @param {TouchEvent} event */
  #handleTouchStart = (event) => {
    const touch = event.touches[0];
    if (!touch) return;
    this.#touchStartX = touch.clientX;
    this.#touchStartY = touch.clientY;
    this.#stop();
  };

  /** @param {TouchEvent} event */
  #handleTouchEnd = (event) => {
    const touch = event.changedTouches[0];
    if (touch && this.#touchStartX !== null && this.#touchStartY !== null) {
      const deltaX = touch.clientX - this.#touchStartX;
      const deltaY = touch.clientY - this.#touchStartY;

      // Only a mostly horizontal swipe changes the testimonial, so vertical page scrolling is left alone
      if (Math.abs(deltaX) > 40 && Math.abs(deltaX) > Math.abs(deltaY) * 1.5) {
        this.show(this.#index + (deltaX < 0 ? 1 : -1));
      }
    }

    this.#touchStartX = null;
    this.#touchStartY = null;
    this.#start();
  };

  #handleVisibility = () => {
    if (document.hidden) this.#stop();
    else this.#start();
  };
}

if (!customElements.get('testimonial-showcase')) {
  customElements.define('testimonial-showcase', TestimonialShowcase);
}
